<template>
  <div class="inscription">
    <div class="inscription__container">
      <h2 class="inscription__title">
        <app-i18n code="entities.inscription.form.title" />
      </h2>
      <div class="inscription__content">
        <!-- Steps -->
        <el-steps
          :active="activeStepIndex"
          align-center
          finish-status="success"
        >
          <el-step :title="i18n('entities.inscription.form.subtitle.info')" />
          <el-step :title="i18n('entities.inscription.form.subtitle.confirmation')" />
        </el-steps>

        <!-- First Step -->
        <div
          class="inscription__step inscription__step--first"
          :style="{ display: activeStepIndex === 0 ? 'block' : 'none' }"
        >
          <!-- Player list -->
          <div class="inscription__players">
            <div class="inscription__players__list">
              <inscription-form
                v-for="(inscription, i) in inscriptions"
                :key="`inscription__players__element--${inscription.index}`"
                :ref="`inscriptionForms`"
                :inscription.sync="inscriptions[i]"
                :is-first="i === 0"
                :is-last="i === (inscriptions.length - 1)"
                :removable="inscriptions.length > 1"
                @deleted="onDeleted(i)"
              />
            </div>

            <!-- Add player -->
            <div
              class="inscription__players__add"
              @click="doAdd"
            >
              <i class="inscription__players__add__icon icon-plus" />
              <app-i18n
                class="inscription__players__add__label"
                code="entities.player.add.one"
              />
            </div>
          </div>
        </div>

        <!-- Second Step -->
        <div
          class="inscription__step inscription__step--second"
          :style="{ display: activeStepIndex === 1 ? 'block' : 'none' }"
          v-loading="saveLoading"
        >
          <el-row
            v-if="activeStepIndex === 1"
            class="inscription__overview"
            :gutter="24"
          >
            <el-col
              :lg="8" :md="12" :sm="12"
              v-for="(inscription, i) in inscriptions"
              :key="`inscription__overview__element--${i}`"
            >
              <div class="inscription__overview__element">
                <p
                  class="inscription__overview__element__name"
                  v-text="`${inscription.model.firstName} ${inscription.model.lastName}`"
                />
                <p class="inscription__overview__element__category">
                  <span
                    class="inscription__overview__element__category__name"
                    v-text="inscription.match.category.name"
                  />
                  <span
                    class="inscription__overview__element__category__years"
                    v-text="getCategoryYearsString(inscription.match.category.minBirthYear, inscription.match.category.maxBirthYear)"
                  />
                </p>
                <p
                  class="inscription__overview__element__program"
                  v-text="programName"
                />
              </div>
            </el-col>
          </el-row>

          <el-form
            class="inscription__user-form"
            v-if="model"
            :rules="rules"
            :model="model"
            :validate-on-rule-change="false"
            ref="userForm"
            @submit.prevent.native="doSubmit"
            @keypress.enter.native="doSubmit"
          >
            
            <!-- <el-row v-if="!userHasIdDocument">
              <el-col :lg="24" :md="24" :sm="24">
                <p
                  class="inscription__user-form__title"
                  v-text="i18n('entities.inscription.overview.parentDocLabel')"
                />
              </el-col>
              <el-col
                v-if="!userHasIdDocument"
                :lg="5" :md="5" :sm="5"
              >
                <el-form-item
                  :label="fields.identDoc.label"
                  :prop="fields.identDoc.name"
                  :required="!fields.identDoc.required"
                >
                  <el-input
                    v-model="model[fields.identDoc.name]"
                    :placeholder="i18n('entities.inscription.placeholders.identDoc')"
                    prefix-icon="icon-document"
                  />
                </el-form-item>
              </el-col>
            </el-row> -->

            <!-- Parents confirmation -->
            <el-row>
              <el-col :lg="24" :md="24" :sm="24">
                <el-form-item
                  :prop="fields.inscriptionTutorConfirmed.name"
                  required
                >
                  <el-checkbox v-model="model[fields.inscriptionTutorConfirmed.name]">
                    <app-i18n :code="fields.inscriptionTutorConfirmed.label" />
                  </el-checkbox>
                </el-form-item>
              </el-col>
            </el-row>
            <el-row>
              <!-- Global error message -->
              <el-col :lg="24" :md="24" :sm="24">
                <el-form-item
                  v-if="globalErrorMessage"
                  :error="globalErrorMessage"
                />
              </el-col>
            </el-row>
          </el-form>
        </div>

        <!-- Form controls -->
        <div
          class="form-buttons form-buttons--sides"
          :class="{ 'form-buttons--sides--right': activeStepIndex === 0 }"
        >
          <!-- Next button -->
          <el-button
            v-if="activeStepIndex !== 1"
            @click="doNext"
            type="primary"
          >
            <app-i18n code="common.next" />
          </el-button>

          <!-- Back button -->
          <el-button
            v-if="activeStepIndex !== 0"
            :disabled="saveLoading"
            @click="doBack"
            type="secondary"
          >
            <app-i18n code="common.backButton" />
          </el-button>

          <!-- Submit button -->
          <el-button
            v-if="activeStepIndex === 1"
            :disabled="saveLoading"
            @click="doSubmit"
            type="primary"
          >
            <app-i18n code="common.confirm" />
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import { isLoggedIn, handleUserAuthChange } from '@/auth'

import { i18n } from '@shared/i18n'
import { FormSchema } from '@shared/form/form-schema'

import createInscriptionsMutation from '@/graphql/mutations/createInscriptions'
import updateUserMutation from '@/graphql/mutations/updateUser'

import InscriptionForm from '@/components/InscriptionForm'

import { UserModel } from '@modules/auth/user-model'

export default {
  name: 'inscriptions',
  data() {
    return {
      currentInscriptionIndex: 0,
      activeStepIndex: 0,
      inscriptions: [],
      saveLoading: false,
      globalErrorMessage: null,
      model: {},
      i18n,
    }
  },
  props: {
    programs: {
      type: Array,
      default: () => ([]),
    },
  },
  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      // userHasIdDocument: 'userHasIdDocument',
      siteTitle: 'title',
    }),
    slug() {
      return this.$route.params.slug
    },
    programId() {
      return this.$route.params.programId
    },
    program() {
      return this.programs.find(program => program.id === this.programId)
    },
    allInscriptionsValid() {
      return this.inscriptions.every(inscription => inscription.valid)
    },
    validInscriptions() {
      return this.inscriptions.filter(inscription => inscription.valid)
    },
    programName() {
      return `${this.siteTitle} · ${this.program.name}`
    },
    userIdentDocRequired() {
      return this.inscriptions.some(i => i.match && i.match.identDocNeeded)
    },
    formSchema() {
      const { fields } = this;
      return new FormSchema([
        // fields.identDoc,
        fields.inscriptionTutorConfirmed,
      ])
    },
    rules() {
      const { formSchema, fields } = this;
      const rules = formSchema.rules()
      if (!this.userIdentDocRequired) delete rules[fields.identDoc.name]
      return rules
    },
    fields() {
      return new UserModel().fields();
    },
  },
  async mounted() {
    const userLoggedIn = await isLoggedIn()
    if (!userLoggedIn) {
      this.setLoginModalVisible(true)
      this.setDialogOnCloseCallback(() => {
        if (!this.currentUser) this.goToHome()
      })
    }
  },
  methods: {
    ...mapMutations({
      setLockDialog: 'setLockDialog',
      setDialogOnCloseCallback: 'setDialogOnCloseCallback',
      setLoginModalVisible: 'setLoginModalVisible',
      setSuccessModalVisible: 'setSuccessModalVisible',
      setSuccessModalType: 'setSuccessModalType',
    }),

    doBack() {
      this.activeStepIndex--
    },

    async doNext() {
      try {
        const forms = [];

        for (const inscriptionForm of this.$refs.inscriptionForms) {
          forms.push(inscriptionForm.$refs.form)
        }

        await Promise.all(forms.map(form => form.validate()))

        if (!this.allInscriptionsValid) return

        this.activeStepIndex++
      } catch (e) {
        return e
      }
    },

    async doSubmit() {
      const uploads = []
      this.globalErrorMessage = null

      try {
        await this.$refs.userForm.validate()
      } catch (e) {
        return
      }

      for (const inscriptionForm of this.$refs.inscriptionForms) {
        uploads.push(inscriptionForm.uploadFiles())
      }

      this.saveLoading = true

      try {
        await Promise.all(uploads)
        await this.$nextTick()

        const inscriptions = this.inscriptions.map(i => i.model)
        await this.$apollo.mutate({
          mutation: createInscriptionsMutation,
          variables: { inscriptions },
        })
  
        const { identDoc } = this.formSchema.cast(this.model)
        const profile = { identDoc }       

        this.setSuccessModalType('inscription')
        this.setSuccessModalVisible(true)
        this.setLockDialog(true)

        await this.$apollo.mutate({
          mutation: updateUserMutation,
          variables: { profile },
        })

        handleUserAuthChange(this.currentUser)
      } catch (e) {
        this.globalErrorMessage = i18n('entities.inscription.overview.unknownError')
        throw e
      } finally {
        this.saveLoading = false
      }
    },

    async doAdd() {
      const inscription = { index: ++this.currentInscriptionIndex }
      
      this.$root.$emit('inscriptions.collapse.all')
      await this.$nextTick()
      this.inscriptions = [inscription, ...this.inscriptions]
    },

    async onDeleted(index) {
      const inscriptions = [...this.inscriptions]
      inscriptions.splice(index, 1)
      await this.$nextTick()
      this.inscriptions = inscriptions
    },

    getCategoryYearsString(minYear, maxYear) {
      const min = Math.min(minYear, maxYear)
      const max = Math.max(minYear, maxYear)

      if (min === max) return i18n('entities.inscription.overview.playersBornIn', min)
      if (max - min === 1) return i18n('entities.inscription.overview.playersBornInAnd', min, max)
      return i18n('entities.inscription.overview.playersBornBetween', min, max)
    },
    
    goToHome() {
      this.$router.push({
        name: 'home',
        params: {
          slug: this.slug,
        }
      })
    },
  },
  watch: {
    currentUser(user) {
      if (user) return
      this.goToHome()
    },
    inscriptions: {
      handler() {
        if (this.inscriptions.length) return
        this.activeStepIndex = 0
        this.inscriptions = [{ index: 0 }]
      },
      immediate: true,
    }
  },
  components: {
    InscriptionForm
  },
}
</script>

<style lang="scss" scoped>
.inscription {
  @include layout-container;

  .el-button {
    @include mobile {
      font-size: 14px;
      min-width: 120px;
      padding: 12px 12px;
    }
  }

  .el-checkbox {
    @include mobile {
      width: 100%;
      white-space: initial;
    }

    &__label {
      @include mobile {
        display: block;
      }
    }
  }

  &__container {
    padding: 0 0 40px;
  }

  &__title {
    font-size: 22px;
    font-weight: normal;
    letter-spacing: -0.3px;
    color: #4c4c4c;
    text-transform: uppercase;
    padding: 32px 24px 24px;
    margin: 0;
    
    @include mobile {
      padding: 16px 8px 8px;
    }

    @include tablet {
      padding: 24px 16px 16px;
    }
  }

  &__content {
    width: 100%;
    box-sizing: border-box;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    background-color: #ffffff;
    padding: 32px;
    
    @include mobile {
      padding: 16px;
    }

    @include tablet {
      padding: 24px;
    }

    body .el-step {
      &__title {
        @include mobile {
          font-size: 6px;
        }
      }
    }
  }

  &__players {
    position: relative;
    width: auto;
    margin: 40px 0 60px;
    background-color: #fbfbfb;
    box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);

    @include mobile {
      margin: 24px 0 32px;
    }

    @include tablet {
      margin: 32px 0 40px;
    }

    &__add {
      width: 100%;
      height: 60px;
      border-top: 1px solid rgba(0, 0, 0, 0.08);
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      padding: 0 24px;
      box-sizing: border-box;

      display: grid;
      grid-template-columns: 20px 1fr;
      grid-gap: 10px;
      align-content: center;

      font-weight: normal;
      letter-spacing: -0.3px;
      background-color: #2acccc;
      color: #ffffff;

      cursor: pointer;

      @include mobile {
        margin: 0 0 32px;
      }

      @include tablet {
        margin: 0 0 32px;
      }

      &__label span {
        font-size: 15px;
      }

      &__icon {
        font-size: 18px;
      }
    }

    &__list {
      .inscription-form:last-of-type {
        .inscription-form__state--collapsed {
          border-bottom: 0;
        }
      }
    }
  }

  &__overview {
    padding: 56px 0 32px;

    @include mobile {
      padding: 16px 12px;
    }

    @include tablet {
      padding: 16px 12px;
    }

    &__element {
      height: 180px;
      border-bottom: 3px solid #2acccc;
      box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);

      display: grid;
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr auto;
      grid-template-areas:
        'name'
        'category'
        'program';
      grid-gap: 16px;
      align-items: center;
      padding: 24px;
      margin-bottom: 24px;

      box-sizing: border-box;
      cursor: default;

      &__name {
        grid-area: name;

        font-size: 20px;
        font-weight: 600;
        letter-spacing: -0.5px;
        color: #333333;

        margin: 0;
      }

      &__category {
        grid-area: category;

        font-size: 40px;
        font-weight: bold;
        letter-spacing: 1px;
        color: #404040;

        margin: 0;

        &__years {
          display: block;
          font-size: 10px;
          font-weight: 300;
          letter-spacing: normal;
          color: rgba(64, 64, 64, 0.35);
        }
      }

      &__program {
        grid-area: program;

        font-size: 14px;
        font-weight: 300;
        color: #404040;

        margin: 0;
      }
    }
  }

  &__user-form {
    padding: 0 0 24px 0;

    &__title {
      font-size: 14px;
      font-weight: normal;
      font-style: normal;
      color: #404040;
      margin-bottom: 24px;
    }

    @include mobile {
      margin: 16px 12px;
    }

    @include tablet {
      margin: 16px 12px;
    }
  }
}
</style>
